import { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Spinner } from '@components/ui';
import { ClientVisibleTasks } from '@components/features';
import { useClientPortalData } from '@hooks';
import { DONE_STATUS, progressOf } from '@utils/portalStats';
import { daysUntil, formatDay, parseDay } from '@utils/calendarDate';

// Apple HIG Liquid Glass Design System
const appleDesign = {
  // Typography - SF Pro inspired sizes
  typography: {
    largeTitle: { fontSize: '34px', fontWeight: '700', letterSpacing: '-0.4px', lineHeight: '1.2' },
    title1: { fontSize: '28px', fontWeight: '700', letterSpacing: '-0.4px', lineHeight: '1.2' },
    title2: { fontSize: '22px', fontWeight: '700', letterSpacing: '-0.3px', lineHeight: '1.3' },
    title3: { fontSize: '20px', fontWeight: '600', letterSpacing: '-0.2px', lineHeight: '1.3' },
    headline: { fontSize: '17px', fontWeight: '600', letterSpacing: '-0.2px', lineHeight: '1.4' },
    body: { fontSize: '17px', fontWeight: '400', letterSpacing: '-0.2px', lineHeight: '1.5' },
    callout: { fontSize: '16px', fontWeight: '400', letterSpacing: '-0.2px', lineHeight: '1.4' },
    subheadline: { fontSize: '15px', fontWeight: '400', letterSpacing: '-0.1px', lineHeight: '1.4' },
    footnote: { fontSize: '13px', fontWeight: '400', letterSpacing: '0px', lineHeight: '1.4' },
    caption1: { fontSize: '12px', fontWeight: '400', letterSpacing: '0px', lineHeight: '1.3' },
    caption2: { fontSize: '11px', fontWeight: '400', letterSpacing: '0.2px', lineHeight: '1.3' },
  },
  // Semantic colors - Apple system colors
  colors: {
    // Backgrounds
    primaryBg: '#ffffff',
    secondaryBg: '#f2f2f7',
    tertiaryBg: '#ffffff',
    groupedBg: '#f2f2f7',
    // Labels
    label: '#000000',
    secondaryLabel: '#3c3c43',
    tertiaryLabel: '#3c3c4399',
    quaternaryLabel: '#3c3c432e',
    // System colors
    systemBlue: '#007aff',
    systemGreen: '#34c759',
    systemIndigo: '#5856d6',
    systemOrange: '#ff9500',
    systemPink: '#ff2d55',
    systemPurple: '#af52de',
    systemRed: '#ff3b30',
    systemTeal: '#5ac8fa',
    systemYellow: '#ffcc00',
    // Separators
    separator: '#3c3c4336',
    opaqueSeparator: '#c6c6c8',
    // Glass effects
    glassLight: 'rgba(255, 255, 255, 0.72)',
    glassDark: 'rgba(0, 0, 0, 0.3)',
  },
  // Spacing based on 8pt grid
  spacing: {
    xs: '4px',
    sm: '8px',
    md: '16px',
    lg: '24px',
    xl: '32px',
    xxl: '48px',
  },
  // Border radius
  radius: {
    sm: '8px',
    md: '12px',
    lg: '16px',
    xl: '20px',
    xxl: '24px',
    full: '9999px',
  },
  // Shadows - Apple style with multiple layers
  shadows: {
    sm: '0 1px 3px rgba(0, 0, 0, 0.08), 0 1px 2px rgba(0, 0, 0, 0.06)',
    md: '0 4px 6px rgba(0, 0, 0, 0.07), 0 2px 4px rgba(0, 0, 0, 0.06)',
    lg: '0 10px 15px rgba(0, 0, 0, 0.1), 0 4px 6px rgba(0, 0, 0, 0.05)',
    xl: '0 20px 25px rgba(0, 0, 0, 0.1), 0 10px 10px rgba(0, 0, 0, 0.04)',
    glass: '0 8px 32px rgba(31, 38, 135, 0.15), inset 0 0 0 1px rgba(255, 255, 255, 0.3)',
  },
  // Liquid Glass styles
  glass: {
    background: 'rgba(255, 255, 255, 0.72)',
    backdropFilter: 'blur(20px) saturate(180%)',
    border: '1px solid rgba(255, 255, 255, 0.5)',
    borderRadius: '20px',
  },
  // Transitions - Apple's fluid animations
  transitions: {
    fast: 'all 0.15s cubic-bezier(0.4, 0, 0.2, 1)',
    normal: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
    slow: 'all 0.35s cubic-bezier(0.4, 0, 0.2, 1)',
    spring: 'all 0.5s cubic-bezier(0.34, 1.56, 0.64, 1)',
  },
  // Touch targets - minimum 44x44pt
  minTouchTarget: '44px',
};

// Liquid Glass Card Component
function GlassCard({ children, padding = 'lg', style = {}, hover = true, ...props }) {
  const [isHovered, setIsHovered] = useState(false);

  const paddingMap = {
    sm: appleDesign.spacing.md,
    md: appleDesign.spacing.lg,
    lg: appleDesign.spacing.xl,
  };

  return (
    <div
      onMouseEnter={() => hover && setIsHovered(true)}
      onMouseLeave={() => hover && setIsHovered(false)}
      style={{
        background: appleDesign.glass.background,
        backdropFilter: appleDesign.glass.backdropFilter,
        WebkitBackdropFilter: appleDesign.glass.backdropFilter,
        border: appleDesign.glass.border,
        borderRadius: appleDesign.glass.borderRadius,
        padding: paddingMap[padding],
        boxShadow: isHovered ? appleDesign.shadows.xl : appleDesign.shadows.lg,
        transition: appleDesign.transitions.normal,
        transform: isHovered ? 'translateY(-2px)' : 'translateY(0)',
        ...style,
      }}
      {...props}
    >
      {children}
    </div>
  );
}

GlassCard.propTypes = {
  children: PropTypes.node,
  padding: PropTypes.oneOf(['sm', 'md', 'lg']),
  style: PropTypes.object,
  hover: PropTypes.bool,
};

// Apple-style Pill Button
function PillButton({ children, active = false, onClick, style = {} }) {
  const [isHovered, setIsHovered] = useState(false);

  return (
    <button
      onClick={onClick}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      style={{
        padding: '10px 20px',
        minHeight: appleDesign.minTouchTarget,
        borderRadius: appleDesign.radius.full,
        border: 'none',
        fontSize: appleDesign.typography.subheadline.fontSize,
        fontWeight: '600',
        cursor: 'pointer',
        transition: appleDesign.transitions.fast,
        backgroundColor: active
          ? appleDesign.colors.systemBlue
          : isHovered
            ? 'rgba(0, 122, 255, 0.1)'
            : 'rgba(118, 118, 128, 0.12)',
        color: active ? '#ffffff' : appleDesign.colors.label,
        transform: isHovered && !active ? 'scale(1.02)' : 'scale(1)',
        ...style,
      }}
    >
      {children}
    </button>
  );
}

PillButton.propTypes = {
  children: PropTypes.node,
  active: PropTypes.bool,
  onClick: PropTypes.func,
  style: PropTypes.object,
};

// Client Timeline Bar Component (read-only version) - Apple Style
function ClientTimelineBar({ epic, progress, weeksToShow, weekOffset = 0, todayWeekIndex = 0 }) {
  const displayStartWeek = (epic.startWeek ?? 0) - weekOffset;
  const displayDuration = epic.duration ?? 4;
  const epicEndWeek = displayStartWeek + displayDuration;
  const isDelayed = progress < 100 && todayWeekIndex >= epicEndWeek;
  const left = (displayStartWeek / weeksToShow) * 100;
  const width = (displayDuration / weeksToShow) * 100;
  const barColor = isDelayed ? appleDesign.colors.systemRed : (epic.color || appleDesign.colors.systemBlue);

  return (
    <div
      style={{
        position: 'absolute',
        left: `${left}%`,
        width: `${width}%`,
        height: '30px',
        top: '3px',
        background: isDelayed ? `${appleDesign.colors.systemRed}12` : appleDesign.glass.background,
        backdropFilter: 'blur(8px)',
        WebkitBackdropFilter: 'blur(8px)',
        borderRadius: appleDesign.radius.md,
        overflow: 'visible',
        border: isDelayed ? `2px solid ${appleDesign.colors.systemRed}` : `1px solid ${appleDesign.colors.separator}`,
        boxShadow: isDelayed ? `0 0 8px ${appleDesign.colors.systemRed}30` : appleDesign.shadows.sm,
        transition: appleDesign.transitions.fast,
      }}
      title={`${epic.name}: ${Math.round(progress)}% completado${isDelayed ? ' - Atrasado' : ''}`}
    >
      {/* Progress fill with gradient */}
      <div
        style={{
          position: 'absolute',
          left: 0,
          top: 0,
          bottom: 0,
          width: `${progress}%`,
          background: `linear-gradient(90deg, ${barColor} 0%, ${barColor}cc 100%)`,
          borderRadius: `${appleDesign.radius.md} 0 0 ${appleDesign.radius.md}`,
          transition: 'width 0.8s cubic-bezier(0.34, 1.56, 0.64, 1)',
        }}
      />
      {/* Epic name */}
      <div
        style={{
          position: 'relative',
          zIndex: 1,
          padding: `0 ${appleDesign.spacing.sm}`,
          height: '100%',
          display: 'flex',
          alignItems: 'center',
          gap: appleDesign.spacing.xs,
          overflow: 'visible',
        }}
      >
        <span
          style={{
            ...appleDesign.typography.caption1,
            fontWeight: '600',
            color: progress > 50 ? '#ffffff' : appleDesign.colors.label,
            whiteSpace: 'nowrap',
            textShadow: progress > 50 ? '0 1px 2px rgba(0,0,0,0.2)' : 'none',
          }}
        >
          {epic.name}
        </span>
        <span
          style={{
            ...appleDesign.typography.caption2,
            color: progress > 50 ? 'rgba(255,255,255,0.9)' : appleDesign.colors.tertiaryLabel,
            flexShrink: 0,
            fontWeight: '600',
          }}
        >
          {Math.round(progress)}%
        </span>
      </div>
    </div>
  );
}

ClientTimelineBar.propTypes = {
  epic: PropTypes.object.isRequired,
  progress: PropTypes.number.isRequired,
  weeksToShow: PropTypes.number.isRequired,
  weekOffset: PropTypes.number,
  todayWeekIndex: PropTypes.number,
};

// Apple-style Progress Ring Component with Liquid Glass effect
function ProgressRing({ percentage, size = 140, strokeWidth = 10, color = appleDesign.colors.systemBlue }) {
  const radius = (size - strokeWidth) / 2;
  const circumference = radius * 2 * Math.PI;
  const offset = circumference - (percentage / 100) * circumference;

  return (
    <div style={{ position: 'relative', width: size, height: size }}>
      {/* Glow effect behind */}
      <div
        style={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: size * 0.7,
          height: size * 0.7,
          borderRadius: '50%',
          background: `radial-gradient(circle, ${color}20 0%, transparent 70%)`,
          filter: 'blur(10px)',
        }}
      />
      <svg width={size} height={size} style={{ transform: 'rotate(-90deg)', position: 'relative', zIndex: 1 }}>
        {/* Background track */}
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke="rgba(255, 255, 255, 0.3)"
          strokeWidth={strokeWidth}
        />
        {/* Progress arc */}
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke={color}
          strokeWidth={strokeWidth}
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          strokeLinecap="round"
          style={{
            transition: 'stroke-dashoffset 0.8s cubic-bezier(0.34, 1.56, 0.64, 1)',
            filter: `drop-shadow(0 0 6px ${color}60)`,
          }}
        />
      </svg>
      {/* Center content */}
      <div
        style={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          textAlign: 'center',
        }}
      >
        <span
          style={{
            ...appleDesign.typography.title1,
            color: '#ffffff',
            display: 'block',
          }}
        >
          {Math.round(percentage)}%
        </span>
      </div>
    </div>
  );
}

// Legacy Progress Circle for cards (lighter style)
function ProgressCircle({ percentage, size = 120, strokeWidth = 8, color = appleDesign.colors.systemBlue }) {
  const radius = (size - strokeWidth) / 2;
  const circumference = radius * 2 * Math.PI;
  const offset = circumference - (percentage / 100) * circumference;

  return (
    <svg width={size} height={size} style={{ transform: 'rotate(-90deg)' }}>
      <circle
        cx={size / 2}
        cy={size / 2}
        r={radius}
        fill="none"
        stroke={appleDesign.colors.separator}
        strokeWidth={strokeWidth}
      />
      <circle
        cx={size / 2}
        cy={size / 2}
        r={radius}
        fill="none"
        stroke={color}
        strokeWidth={strokeWidth}
        strokeDasharray={circumference}
        strokeDashoffset={offset}
        strokeLinecap="round"
        style={{
          transition: 'stroke-dashoffset 0.8s cubic-bezier(0.34, 1.56, 0.64, 1)',
          filter: `drop-shadow(0 0 4px ${color}40)`,
        }}
      />
      <text
        x="50%"
        y="50%"
        textAnchor="middle"
        dy="0.3em"
        style={{
          transform: 'rotate(90deg)',
          transformOrigin: 'center',
          ...appleDesign.typography.title2,
          fill: appleDesign.colors.label,
        }}
      >
        {Math.round(percentage)}%
      </text>
    </svg>
  );
}

ProgressCircle.propTypes = {
  percentage: PropTypes.number.isRequired,
  size: PropTypes.number,
  strokeWidth: PropTypes.number,
  color: PropTypes.string,
};

// Apple-style Activity Item Component with SF Symbols-inspired icons
function ActivityItem({ activity }) {
  const iconConfig = {
    task_created: { icon: '✦', bg: appleDesign.colors.systemBlue, color: '#fff' },
    task_completed: { icon: '✓', bg: appleDesign.colors.systemGreen, color: '#fff' },
    sprint_started: { icon: '▶', bg: appleDesign.colors.systemIndigo, color: '#fff' },
    sprint_completed: { icon: '★', bg: appleDesign.colors.systemPurple, color: '#fff' },
    milestone_reached: { icon: '◎', bg: appleDesign.colors.systemOrange, color: '#fff' },
    progress_update: { icon: '↗', bg: appleDesign.colors.systemTeal, color: '#fff' },
  };

  const config = iconConfig[activity.type] || { icon: '●', bg: appleDesign.colors.secondaryBg, color: appleDesign.colors.label };

  return (
    <div
      style={{
        display: 'flex',
        gap: appleDesign.spacing.md,
        padding: `${appleDesign.spacing.md} 0`,
        borderBottom: `1px solid ${appleDesign.colors.separator}`,
        transition: appleDesign.transitions.fast,
      }}
    >
      <div
        style={{
          width: appleDesign.minTouchTarget,
          height: appleDesign.minTouchTarget,
          borderRadius: appleDesign.radius.md,
          backgroundColor: config.bg,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: '18px',
          fontWeight: '700',
          color: config.color,
          flexShrink: 0,
          boxShadow: `0 2px 8px ${config.bg}40`,
        }}
      >
        {config.icon}
      </div>
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
        <p style={{ ...appleDesign.typography.subheadline, color: appleDesign.colors.label, margin: 0 }}>
          {activity.message}
        </p>
        <p style={{ ...appleDesign.typography.caption1, color: appleDesign.colors.tertiaryLabel, margin: '4px 0 0 0' }}>
          {activity.date}
        </p>
      </div>
    </div>
  );
}

ActivityItem.propTypes = {
  activity: PropTypes.object.isRequired,
};

export default function ClientPortal({ project, onBack, isPublicPortal = false, portalToken, clientInfo }) {
  const projectId = project?.id;

  const { visibleTasks, stats, clientStatuses, sprints, epics, isLoading } = useClientPortalData({
    projectId,
    isPublicPortal,
    portalToken,
  });

  // Calculate metrics
  const metrics = useMemo(() => {
    const { totalTasks, completedTasks } = stats;
    const progress = progressOf({ total: totalTasks, completed: completedTasks });

    // Find active sprint by status OR by date range
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const activeSprint = sprints.find((s) => {
      // First check explicit ACTIVE status
      if (s.status?.toUpperCase() === 'ACTIVE') return true;
      // Then check if current date is within sprint date range
      if (s.startDate && s.endDate) {
        const start = parseDay(s.startDate);
        const end = parseDay(s.endDate);
        end.setHours(23, 59, 59, 999);
        return today >= start && today <= end;
      }
      return false;
    });
    const sprintStats = activeSprint ? stats.bySprint[activeSprint.id] : null;
    const sprintProgress = progressOf(sprintStats);

    // Days remaining in sprint
    let daysRemaining = 0;
    if (activeSprint?.endDate) {
      daysRemaining = Math.max(0, daysUntil(activeSprint.endDate));
    }

    return {
      totalTasks,
      completedTasks,
      progress,
      activeSprint,
      sprintProgress,
      daysRemaining,
      sprintTasks: sprintStats?.total ?? 0,
    };
  }, [stats, sprints]);

  // Generate activity feed from tasks and sprints
  const activities = useMemo(() => {
    const feed = [];

    // Get recently completed tasks
    const recentDone = visibleTasks
      .filter((t) => t.status === DONE_STATUS)
      .slice(0, 5)
      .map((t) => ({
        type: 'task_completed',
        message: `Tarea completada: "${t.title}"`,
        date: new Date(t.updatedAt).toLocaleDateString('es-ES', {
          day: 'numeric',
          month: 'short',
        }),
        timestamp: new Date(t.updatedAt),
      }));

    feed.push(...recentDone);

    // Add sprint info
    if (metrics.activeSprint) {
      feed.push({
        type: 'sprint_started',
        message: `Sprint activo: ${metrics.activeSprint.name}`,
        date: metrics.activeSprint.startDate
          ? formatDay(metrics.activeSprint.startDate, { day: 'numeric', month: 'short' })
          : '',
        timestamp: metrics.activeSprint.startDate ? parseDay(metrics.activeSprint.startDate) : new Date(0),
      });
    }

    // Sort by timestamp
    return feed.sort((a, b) => b.timestamp - a.timestamp).slice(0, 8);
  }, [visibleTasks, metrics.activeSprint]);

  // Epic progress for roadmap preview
  const epicProgress = useMemo(() => {
    return epics.map((epic) => {
      const epicStats = stats.byEpic[epic.id];
      return {
        ...epic,
        progress: progressOf(epicStats),
        taskCount: epicStats?.total ?? 0,
        completedCount: epicStats?.completed ?? 0,
      };
    });
  }, [epics, stats]);

  // Timeline configuration for roadmap - starts from earliest epic
  const timelineConfig = useMemo(() => {
    const now = new Date();

    // Use project start date as base reference, fallback to today
    let baseDate;
    if (project?.startDate) {
      baseDate = parseDay(project.startDate);
    } else {
      baseDate = new Date(now);
    }

    // Adjust baseDate to Monday of its week
    const baseDayOfWeek = baseDate.getDay();
    const baseDaysToSubtract = baseDayOfWeek === 0 ? 6 : baseDayOfWeek - 1;
    const baseMonday = new Date(baseDate);
    baseMonday.setDate(baseDate.getDate() - baseDaysToSubtract);
    baseMonday.setHours(0, 0, 0, 0);

    // Find the earliest epic startWeek
    const minStartWeek = epics.length > 0
      ? Math.min(...epics.map(e => e.startWeek ?? 0))
      : 0;

    // Find the latest epic end (startWeek + duration)
    const maxEndWeek = epics.length > 0
      ? Math.max(...epics.map(e => (e.startWeek ?? 0) + (e.duration ?? 4)))
      : minStartWeek + 24;

    // Start timeline from the earliest epic's week
    const startOfWeek = new Date(baseMonday);
    startOfWeek.setDate(baseMonday.getDate() + (minStartWeek * 7));

    // Ensure enough weeks to cover all epics plus some padding
    const weeksToShow = Math.max(24, maxEndWeek - minStartWeek + 4);
    const weeks = [];

    // Calculate current week's Monday for comparison
    const nowMonday = new Date(now);
    const nowDayOfWeek = now.getDay();
    const nowDaysToSubtract = nowDayOfWeek === 0 ? 6 : nowDayOfWeek - 1;
    nowMonday.setDate(now.getDate() - nowDaysToSubtract);
    nowMonday.setHours(0, 0, 0, 0);

    let lastYear = null;
    for (let i = 0; i < weeksToShow; i++) {
      const weekStart = new Date(startOfWeek);
      weekStart.setDate(startOfWeek.getDate() + (i * 7));
      const year = weekStart.getFullYear();
      const showYear = year !== lastYear;
      lastYear = year;

      const isCurrentWeek = weekStart.getTime() === nowMonday.getTime();

      weeks.push({
        start: weekStart,
        label: weekStart.toLocaleDateString('es-ES', { day: 'numeric', month: 'short' }),
        year: showYear ? year : null,
        isCurrentWeek,
      });
    }

    // Calculate today's exact position as percentage and week index
    const msPerWeek = 7 * 24 * 60 * 60 * 1000;
    const msSinceStart = now.getTime() - startOfWeek.getTime();
    const todayPositionPercent = Math.max(0, Math.min(100, (msSinceStart / msPerWeek / weeksToShow) * 100));
    const todayWeekIndex = Math.floor(msSinceStart / msPerWeek);

    return { weeks, weeksToShow, startDate: startOfWeek, weekOffset: minStartWeek, todayPositionPercent, todayWeekIndex };
  }, [epics, project?.startDate]);

  // Calculate parallel work zones (adjusted for weekOffset)
  const parallelZones = useMemo(() => {
    const weekCounts = {};
    const offset = timelineConfig.weekOffset;

    epics.forEach(epic => {
      const startWeek = (epic.startWeek || 0) - offset;
      const duration = epic.duration || 4;

      for (let w = startWeek; w < startWeek + duration && w < timelineConfig.weeksToShow; w++) {
        if (w >= 0) {
          weekCounts[w] = (weekCounts[w] || 0) + 1;
        }
      }
    });

    const zones = [];
    let currentZone = null;

    for (let w = 0; w < timelineConfig.weeksToShow; w++) {
      const count = weekCounts[w] || 0;
      if (count >= 2) {
        if (!currentZone) {
          currentZone = { start: w, count };
        }
        currentZone.end = w;
        currentZone.maxCount = Math.max(currentZone.maxCount || count, count);
      } else if (currentZone) {
        zones.push(currentZone);
        currentZone = null;
      }
    }
    if (currentZone) zones.push(currentZone);

    return zones;
  }, [epics, timelineConfig.weeksToShow, timelineConfig.weekOffset]);

  // State for roadmap view mode
  const [roadmapView, setRoadmapView] = useState('timeline'); // 'timeline' or 'list'

  if (isLoading) {
    return (
      <div
        style={{
          minHeight: '100vh',
          background: `linear-gradient(135deg, ${appleDesign.colors.systemIndigo} 0%, ${appleDesign.colors.systemPurple} 100%)`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <GlassCard padding="lg" hover={false} style={{ textAlign: 'center', minWidth: '280px' }}>
          <Spinner size="lg" />
          <p style={{ ...appleDesign.typography.body, color: appleDesign.colors.secondaryLabel, marginTop: appleDesign.spacing.md }}>
            Cargando portal del cliente...
          </p>
        </GlassCard>
      </div>
    );
  }

  return (
    <div
      style={{
        minHeight: '100vh',
        backgroundColor: appleDesign.colors.groupedBg,
        fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", "SF Pro Text", "Helvetica Neue", Arial, sans-serif',
      }}
    >
      {/* Apple-style Header with Liquid Glass */}
      <header
        style={{
          background: `linear-gradient(135deg, ${appleDesign.colors.systemIndigo} 0%, ${appleDesign.colors.systemPurple} 50%, ${appleDesign.colors.systemPink}80 100%)`,
          padding: `${appleDesign.spacing.xl} ${appleDesign.spacing.lg}`,
          color: 'white',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        {/* Decorative blur circles - Apple style */}
        <div
          style={{
            position: 'absolute',
            top: '-100px',
            right: '-100px',
            width: '400px',
            height: '400px',
            borderRadius: '50%',
            background: 'radial-gradient(circle, rgba(255,255,255,0.15) 0%, transparent 70%)',
            pointerEvents: 'none',
          }}
        />
        <div
          style={{
            position: 'absolute',
            bottom: '-150px',
            left: '-50px',
            width: '300px',
            height: '300px',
            borderRadius: '50%',
            background: 'radial-gradient(circle, rgba(255,255,255,0.1) 0%, transparent 70%)',
            pointerEvents: 'none',
          }}
        />

        <div style={{ maxWidth: '1200px', margin: '0 auto', position: 'relative', zIndex: 1 }}>
          {/* Top bar */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: appleDesign.spacing.xl }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: appleDesign.spacing.md }}>
              {clientInfo?.logo ? (
                <img
                  src={clientInfo.logo}
                  alt={clientInfo.name || 'Logo'}
                  style={{
                    width: '56px',
                    height: '56px',
                    borderRadius: appleDesign.radius.lg,
                    objectFit: 'cover',
                    backgroundColor: 'rgba(255,255,255,0.2)',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                  }}
                  onError={(e) => {
                    e.target.style.display = 'none';
                  }}
                />
              ) : (
                <div
                  style={{
                    width: '56px',
                    height: '56px',
                    background: 'rgba(255,255,255,0.2)',
                    backdropFilter: 'blur(10px)',
                    WebkitBackdropFilter: 'blur(10px)',
                    borderRadius: appleDesign.radius.lg,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '28px',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                  }}
                >
                  ◈
                </div>
              )}
              <div>
                <h1 style={{ ...appleDesign.typography.title2, margin: 0, color: '#ffffff' }}>
                  {clientInfo?.name ? `Portal de ${clientInfo.name}` : 'Portal del Cliente'}
                </h1>
                <p style={{ ...appleDesign.typography.subheadline, opacity: 0.85, margin: '4px 0 0 0' }}>
                  {project?.name}
                </p>
              </div>
            </div>
{!isPublicPortal && (
              <button
                onClick={onBack}
                style={{
                  padding: '12px 24px',
                  minHeight: appleDesign.minTouchTarget,
                  background: 'rgba(255,255,255,0.2)',
                  backdropFilter: 'blur(10px)',
                  WebkitBackdropFilter: 'blur(10px)',
                  border: '1px solid rgba(255,255,255,0.3)',
                  borderRadius: appleDesign.radius.full,
                  color: 'white',
                  ...appleDesign.typography.subheadline,
                  fontWeight: '600',
                  cursor: 'pointer',
                  transition: appleDesign.transitions.fast,
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                }}
                onMouseOver={(e) => {
                  e.currentTarget.style.background = 'rgba(255,255,255,0.3)';
                  e.currentTarget.style.transform = 'scale(1.02)';
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.background = 'rgba(255,255,255,0.2)';
                  e.currentTarget.style.transform = 'scale(1)';
                }}
              >
                <span style={{ fontSize: '16px' }}>←</span>
                Volver
              </button>
            )}
          </div>

          {/* Progress Overview - Liquid Glass Card */}
          <div
            style={{
              background: 'rgba(255,255,255,0.15)',
              backdropFilter: 'blur(20px) saturate(180%)',
              WebkitBackdropFilter: 'blur(20px) saturate(180%)',
              borderRadius: appleDesign.radius.xxl,
              border: '1px solid rgba(255,255,255,0.25)',
              padding: appleDesign.spacing.xl,
              display: 'flex',
              alignItems: 'center',
              gap: appleDesign.spacing.xl,
              boxShadow: '0 8px 32px rgba(0,0,0,0.1), inset 0 1px 0 rgba(255,255,255,0.2)',
            }}
          >
            <ProgressRing percentage={metrics.progress} size={140} strokeWidth={10} color="#ffffff" />
            <div style={{ flex: 1 }}>
              <h2 style={{ ...appleDesign.typography.largeTitle, margin: '0 0 8px 0', color: '#ffffff' }}>
                Progreso General
              </h2>
              <p style={{ ...appleDesign.typography.body, opacity: 0.9, margin: 0 }}>
                <span style={{ fontWeight: '700', fontSize: '20px' }}>{metrics.completedTasks}</span> de{' '}
                <span style={{ fontWeight: '700', fontSize: '20px' }}>{metrics.totalTasks}</span> tareas completadas
              </p>
              {metrics.activeSprint && (
                <div
                  style={{
                    marginTop: appleDesign.spacing.md,
                    padding: '8px 16px',
                    background: 'rgba(255,255,255,0.15)',
                    borderRadius: appleDesign.radius.full,
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '8px',
                  }}
                >
                  <span style={{ width: '8px', height: '8px', borderRadius: '50%', backgroundColor: appleDesign.colors.systemGreen, animation: 'pulse 2s infinite' }} />
                  <span style={{ ...appleDesign.typography.caption1, fontWeight: '600' }}>
                    Sprint activo: {metrics.activeSprint.name}
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Pulse animation keyframes via style tag */}
      <style>
        {`
          @keyframes pulse {
            0%, 100% { opacity: 1; transform: scale(1); }
            50% { opacity: 0.6; transform: scale(1.1); }
          }
        `}
      </style>

      {/* Main Content */}
      <main style={{ maxWidth: '1200px', margin: '0 auto', padding: appleDesign.spacing.lg }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(340px, 1fr))', gap: appleDesign.spacing.lg }}>
          {/* Sprint Actual Card - Apple Style */}
          <GlassCard padding="lg">
            <div style={{ display: 'flex', alignItems: 'center', gap: appleDesign.spacing.md, marginBottom: appleDesign.spacing.lg }}>
              <div
                style={{
                  width: '48px',
                  height: '48px',
                  borderRadius: appleDesign.radius.md,
                  background: `linear-gradient(135deg, ${appleDesign.colors.systemIndigo} 0%, ${appleDesign.colors.systemPurple} 100%)`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  boxShadow: `0 4px 12px ${appleDesign.colors.systemIndigo}40`,
                }}
              >
                <span style={{ color: '#fff', fontSize: '22px', fontWeight: '700' }}>▶</span>
              </div>
              <div>
                <h3 style={{ ...appleDesign.typography.headline, color: appleDesign.colors.label, margin: 0 }}>Sprint Actual</h3>
                <p style={{ ...appleDesign.typography.caption1, color: appleDesign.colors.tertiaryLabel, margin: '2px 0 0 0' }}>
                  Ciclo de desarrollo
                </p>
              </div>
            </div>
            {metrics.activeSprint ? (
              <>
                <h4 style={{ ...appleDesign.typography.title3, color: appleDesign.colors.label, margin: `0 0 ${appleDesign.spacing.md} 0` }}>
                  {metrics.activeSprint.name}
                </h4>
                {/* Apple-style progress bar */}
                <div
                  style={{
                    backgroundColor: appleDesign.colors.separator,
                    borderRadius: appleDesign.radius.full,
                    height: '10px',
                    overflow: 'hidden',
                    marginBottom: appleDesign.spacing.md,
                    boxShadow: 'inset 0 1px 2px rgba(0,0,0,0.1)',
                  }}
                >
                  <div
                    style={{
                      height: '100%',
                      background: `linear-gradient(90deg, ${appleDesign.colors.systemIndigo} 0%, ${appleDesign.colors.systemPurple} 100%)`,
                      width: `${metrics.sprintProgress}%`,
                      borderRadius: appleDesign.radius.full,
                      transition: 'width 0.8s cubic-bezier(0.34, 1.56, 0.64, 1)',
                      boxShadow: `0 0 10px ${appleDesign.colors.systemIndigo}60`,
                    }}
                  />
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ ...appleDesign.typography.footnote, color: appleDesign.colors.secondaryLabel }}>
                    <strong style={{ color: appleDesign.colors.systemIndigo }}>{Math.round(metrics.sprintProgress)}%</strong> completado
                  </span>
                  <span
                    style={{
                      ...appleDesign.typography.caption1,
                      padding: '6px 12px',
                      backgroundColor: metrics.daysRemaining <= 3 ? `${appleDesign.colors.systemOrange}15` : `${appleDesign.colors.systemGreen}15`,
                      color: metrics.daysRemaining <= 3 ? appleDesign.colors.systemOrange : appleDesign.colors.systemGreen,
                      borderRadius: appleDesign.radius.full,
                      fontWeight: '600',
                    }}
                  >
                    {metrics.daysRemaining} días restantes
                  </span>
                </div>
                {metrics.activeSprint.goal && (
                  <div
                    style={{
                      marginTop: appleDesign.spacing.md,
                      padding: appleDesign.spacing.md,
                      backgroundColor: appleDesign.colors.secondaryBg,
                      borderRadius: appleDesign.radius.md,
                      borderLeft: `3px solid ${appleDesign.colors.systemIndigo}`,
                    }}
                  >
                    <p style={{ ...appleDesign.typography.footnote, color: appleDesign.colors.secondaryLabel, margin: 0, fontStyle: 'italic' }}>
                      "{metrics.activeSprint.goal}"
                    </p>
                  </div>
                )}
              </>
            ) : (
              <div style={{ textAlign: 'center', padding: appleDesign.spacing.xl }}>
                <div style={{ fontSize: '48px', marginBottom: appleDesign.spacing.md, opacity: 0.3 }}>◇</div>
                <p style={{ ...appleDesign.typography.subheadline, color: appleDesign.colors.tertiaryLabel }}>
                  No hay sprint activo actualmente
                </p>
              </div>
            )}
          </GlassCard>

          {/* Stats Cards - Apple Style Grid */}
          <GlassCard padding="lg">
            <div style={{ display: 'flex', alignItems: 'center', gap: appleDesign.spacing.md, marginBottom: appleDesign.spacing.lg }}>
              <div
                style={{
                  width: '48px',
                  height: '48px',
                  borderRadius: appleDesign.radius.md,
                  background: `linear-gradient(135deg, ${appleDesign.colors.systemBlue} 0%, ${appleDesign.colors.systemTeal} 100%)`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  boxShadow: `0 4px 12px ${appleDesign.colors.systemBlue}40`,
                }}
              >
                <span style={{ color: '#fff', fontSize: '22px', fontWeight: '700' }}>☰</span>
              </div>
              <div>
                <h3 style={{ ...appleDesign.typography.headline, color: appleDesign.colors.label, margin: 0 }}>Resumen</h3>
                <p style={{ ...appleDesign.typography.caption1, color: appleDesign.colors.tertiaryLabel, margin: '2px 0 0 0' }}>
                  Métricas del proyecto
                </p>
              </div>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: appleDesign.spacing.md }}>
              {/* Completed */}
              <div
                style={{
                  textAlign: 'center',
                  padding: appleDesign.spacing.md,
                  background: `linear-gradient(135deg, ${appleDesign.colors.systemGreen}10 0%, ${appleDesign.colors.systemGreen}05 100%)`,
                  borderRadius: appleDesign.radius.lg,
                  border: `1px solid ${appleDesign.colors.systemGreen}20`,
                  transition: appleDesign.transitions.fast,
                }}
              >
                <p style={{ ...appleDesign.typography.largeTitle, color: appleDesign.colors.systemGreen, margin: 0 }}>
                  {metrics.completedTasks}
                </p>
                <p style={{ ...appleDesign.typography.caption1, color: appleDesign.colors.secondaryLabel, margin: '4px 0 0 0' }}>
                  Completadas
                </p>
              </div>
              {/* Pending */}
              <div
                style={{
                  textAlign: 'center',
                  padding: appleDesign.spacing.md,
                  background: `linear-gradient(135deg, ${appleDesign.colors.systemOrange}10 0%, ${appleDesign.colors.systemOrange}05 100%)`,
                  borderRadius: appleDesign.radius.lg,
                  border: `1px solid ${appleDesign.colors.systemOrange}20`,
                  transition: appleDesign.transitions.fast,
                }}
              >
                <p style={{ ...appleDesign.typography.largeTitle, color: appleDesign.colors.systemOrange, margin: 0 }}>
                  {metrics.totalTasks - metrics.completedTasks}
                </p>
                <p style={{ ...appleDesign.typography.caption1, color: appleDesign.colors.secondaryLabel, margin: '4px 0 0 0' }}>
                  Pendientes
                </p>
              </div>
              {/* Sprints */}
              <div
                style={{
                  textAlign: 'center',
                  padding: appleDesign.spacing.md,
                  background: `linear-gradient(135deg, ${appleDesign.colors.systemPurple}10 0%, ${appleDesign.colors.systemPurple}05 100%)`,
                  borderRadius: appleDesign.radius.lg,
                  border: `1px solid ${appleDesign.colors.systemPurple}20`,
                  transition: appleDesign.transitions.fast,
                }}
              >
                <p style={{ ...appleDesign.typography.largeTitle, color: appleDesign.colors.systemPurple, margin: 0 }}>
                  {sprints.filter((s) => s.status?.toUpperCase() === 'COMPLETED').length}
                </p>
                <p style={{ ...appleDesign.typography.caption1, color: appleDesign.colors.secondaryLabel, margin: '4px 0 0 0' }}>
                  Sprints
                </p>
              </div>
              {/* Epics */}
              <div
                style={{
                  textAlign: 'center',
                  padding: appleDesign.spacing.md,
                  background: `linear-gradient(135deg, ${appleDesign.colors.systemBlue}10 0%, ${appleDesign.colors.systemBlue}05 100%)`,
                  borderRadius: appleDesign.radius.lg,
                  border: `1px solid ${appleDesign.colors.systemBlue}20`,
                  transition: appleDesign.transitions.fast,
                }}
              >
                <p style={{ ...appleDesign.typography.largeTitle, color: appleDesign.colors.systemBlue, margin: 0 }}>
                  {epics.length}
                </p>
                <p style={{ ...appleDesign.typography.caption1, color: appleDesign.colors.secondaryLabel, margin: '4px 0 0 0' }}>
                  Épicas
                </p>
              </div>
            </div>
          </GlassCard>

          {/* Activity Feed - Apple Style */}
          <GlassCard padding="lg">
            <div style={{ display: 'flex', alignItems: 'center', gap: appleDesign.spacing.md, marginBottom: appleDesign.spacing.lg }}>
              <div
                style={{
                  width: '48px',
                  height: '48px',
                  borderRadius: appleDesign.radius.md,
                  background: `linear-gradient(135deg, ${appleDesign.colors.systemPink} 0%, ${appleDesign.colors.systemRed} 100%)`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  boxShadow: `0 4px 12px ${appleDesign.colors.systemPink}40`,
                }}
              >
                <span style={{ color: '#fff', fontSize: '22px', fontWeight: '700' }}>◉</span>
              </div>
              <div>
                <h3 style={{ ...appleDesign.typography.headline, color: appleDesign.colors.label, margin: 0 }}>Actualizaciones</h3>
                <p style={{ ...appleDesign.typography.caption1, color: appleDesign.colors.tertiaryLabel, margin: '2px 0 0 0' }}>
                  Actividad reciente
                </p>
              </div>
            </div>
            <div
              style={{
                maxHeight: '320px',
                overflowY: 'auto',
                marginRight: '-8px',
                paddingRight: '8px',
              }}
            >
              {activities.length > 0 ? (
                activities.map((activity, index) => (
                  <ActivityItem key={index} activity={activity} />
                ))
              ) : (
                <div style={{ textAlign: 'center', padding: appleDesign.spacing.xl }}>
                  <div style={{ fontSize: '48px', marginBottom: appleDesign.spacing.md, opacity: 0.3 }}>◇</div>
                  <p style={{ ...appleDesign.typography.subheadline, color: appleDesign.colors.tertiaryLabel }}>
                    No hay actualizaciones recientes
                  </p>
                </div>
              )}
            </div>
          </GlassCard>
        </div>

        {/* Tasks in statuses marked as visible to the client */}
        <ClientVisibleTasks statuses={clientStatuses} tasks={visibleTasks} />

        {/* Full Roadmap Timeline Section - Apple Style */}
        <GlassCard padding="lg" style={{ marginTop: appleDesign.spacing.lg }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: appleDesign.spacing.lg }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: appleDesign.spacing.md }}>
              <div
                style={{
                  width: '48px',
                  height: '48px',
                  borderRadius: appleDesign.radius.md,
                  background: `linear-gradient(135deg, ${appleDesign.colors.systemTeal} 0%, ${appleDesign.colors.systemBlue} 100%)`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  boxShadow: `0 4px 12px ${appleDesign.colors.systemTeal}40`,
                }}
              >
                <span style={{ color: '#fff', fontSize: '22px', fontWeight: '700' }}>◫</span>
              </div>
              <div>
                <h3 style={{ ...appleDesign.typography.headline, color: appleDesign.colors.label, margin: 0 }}>Roadmap del Proyecto</h3>
                <p style={{ ...appleDesign.typography.caption1, color: appleDesign.colors.tertiaryLabel, margin: '2px 0 0 0' }}>
                  Planificación y progreso
                </p>
              </div>
            </div>
            {/* Apple-style segmented control */}
            <div
              style={{
                display: 'flex',
                gap: '4px',
                padding: '4px',
                backgroundColor: 'rgba(118, 118, 128, 0.12)',
                borderRadius: appleDesign.radius.md,
              }}
            >
              <PillButton active={roadmapView === 'timeline'} onClick={() => setRoadmapView('timeline')} style={{ borderRadius: appleDesign.radius.sm, padding: '8px 16px', minHeight: '36px' }}>
                Timeline
              </PillButton>
              <PillButton active={roadmapView === 'list'} onClick={() => setRoadmapView('list')} style={{ borderRadius: appleDesign.radius.sm, padding: '8px 16px', minHeight: '36px' }}>
                Lista
              </PillButton>
            </div>
          </div>

          {roadmapView === 'timeline' ? (
            <>
              {/* Timeline View - Apple Style */}
              <div
                style={{
                  maxHeight: '420px',
                  overflowY: 'auto',
                  overflowX: 'auto',
                  border: `1px solid ${appleDesign.colors.separator}`,
                  borderRadius: appleDesign.radius.lg,
                  backgroundColor: appleDesign.colors.primaryBg,
                }}
              >
                <div style={{ minWidth: `${160 + (timelineConfig.weeksToShow * 50) + 16}px` }}>
                  {/* Timeline header */}
                  <div
                    style={{
                      display: 'flex',
                      borderBottom: `1px solid ${appleDesign.colors.separator}`,
                      backgroundColor: appleDesign.colors.primaryBg,
                      position: 'sticky',
                      top: 0,
                      zIndex: 10,
                    }}
                  >
                    <div style={{ width: '160px', flexShrink: 0, padding: '10px 0', backgroundColor: appleDesign.colors.primaryBg }} />
                    <div style={{ display: 'flex', width: `${timelineConfig.weeksToShow * 50}px`, position: 'relative' }}>
                      {timelineConfig.weeks.map((week, i) => (
                        <div
                          key={i}
                          style={{
                            width: '50px',
                            flexShrink: 0,
                            textAlign: 'center',
                            padding: '6px 2px',
                            ...appleDesign.typography.caption2,
                            color: week.isCurrentWeek ? appleDesign.colors.systemBlue : appleDesign.colors.tertiaryLabel,
                            fontWeight: week.isCurrentWeek ? '600' : '400',
                            borderLeft: i > 0 ? `1px solid ${appleDesign.colors.separator}` : 'none',
                            backgroundColor: week.isCurrentWeek ? `${appleDesign.colors.systemBlue}08` : appleDesign.colors.primaryBg,
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'center',
                            alignItems: 'center',
                            gap: '1px',
                          }}
                        >
                          {week.year && (
                            <span style={{
                              ...appleDesign.typography.caption2,
                              fontWeight: '700',
                              color: week.isCurrentWeek ? appleDesign.colors.systemBlue : appleDesign.colors.secondaryLabel,
                              lineHeight: '1',
                            }}>
                              {week.year}
                            </span>
                          )}
                          <span>{week.label}</span>
                        </div>
                      ))}
                      {/* Today marker in header */}
                      {timelineConfig.todayPositionPercent > 0 && timelineConfig.todayPositionPercent < 100 && (
                        <div
                          style={{
                            position: 'absolute',
                            left: `${timelineConfig.todayPositionPercent}%`,
                            top: 0,
                            bottom: 0,
                            width: '2px',
                            backgroundColor: appleDesign.colors.systemRed,
                            zIndex: 20,
                            pointerEvents: 'none',
                          }}
                        />
                      )}
                    </div>
                  </div>

                  {/* Epic rows */}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', padding: '8px 0' }}>
                    {epics.map((epic) => {
                      const epicProgress = progressOf(stats.byEpic[epic.id]);
                      const epicEndWeek = ((epic.startWeek ?? 0) - timelineConfig.weekOffset) + (epic.duration ?? 4);
                      const isEpicDelayed = epicProgress < 100 && timelineConfig.todayWeekIndex >= epicEndWeek;

                      return (
                      <div key={epic.id} style={{ display: 'flex', alignItems: 'center', padding: '4px 0' }}>
                        <div
                          style={{
                            width: '160px',
                            flexShrink: 0,
                            paddingRight: appleDesign.spacing.md,
                            paddingLeft: appleDesign.spacing.md,
                            position: 'sticky',
                            left: 0,
                            backgroundColor: appleDesign.colors.primaryBg,
                            zIndex: 5,
                          }}
                        >
                          <div style={{ display: 'flex', alignItems: 'center', gap: appleDesign.spacing.sm }}>
                            <div
                              style={{
                                width: '10px',
                                height: '10px',
                                borderRadius: appleDesign.radius.sm,
                                backgroundColor: isEpicDelayed ? appleDesign.colors.systemRed : (epic.color || appleDesign.colors.systemBlue),
                                flexShrink: 0,
                                boxShadow: `0 2px 4px ${isEpicDelayed ? appleDesign.colors.systemRed : (epic.color || appleDesign.colors.systemBlue)}40`,
                              }}
                            />
                            <span
                              style={{
                                ...appleDesign.typography.caption1,
                                fontWeight: '500',
                                color: isEpicDelayed ? appleDesign.colors.systemRed : appleDesign.colors.label,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                              }}
                            >
                              {epic.name}
                            </span>
                            {isEpicDelayed && (
                              <span
                                style={{
                                  ...appleDesign.typography.caption2,
                                  fontWeight: '600',
                                  color: appleDesign.colors.systemRed,
                                  backgroundColor: `${appleDesign.colors.systemRed}12`,
                                  padding: '1px 6px',
                                  borderRadius: appleDesign.radius.full,
                                  flexShrink: 0,
                                  lineHeight: '1.4',
                                }}
                              >
                                Atrasado
                              </span>
                            )}
                          </div>
                        </div>

                        <div
                          style={{
                            width: `${timelineConfig.weeksToShow * 50}px`,
                            flexShrink: 0,
                            height: '36px',
                            position: 'relative',
                            backgroundColor: appleDesign.colors.secondaryBg,
                            borderRadius: appleDesign.radius.sm,
                            marginRight: appleDesign.spacing.sm,
                          }}
                        >
                          {/* Parallel work zone indicators */}
                          {parallelZones.map((zone, idx) => (
                            <div
                              key={idx}
                              style={{
                                position: 'absolute',
                                left: `${(zone.start / timelineConfig.weeksToShow) * 100}%`,
                                width: `${((zone.end - zone.start + 1) / timelineConfig.weeksToShow) * 100}%`,
                                top: 0,
                                bottom: 0,
                                backgroundColor: zone.maxCount >= 3 ? `${appleDesign.colors.systemOrange}15` : `${appleDesign.colors.systemIndigo}10`,
                                borderLeft: '2px dashed',
                                borderRight: '2px dashed',
                                borderColor: zone.maxCount >= 3 ? appleDesign.colors.systemOrange : appleDesign.colors.systemIndigo,
                                pointerEvents: 'none',
                              }}
                              title={`${zone.maxCount} épicas en paralelo`}
                            />
                          ))}
                          {/* Today marker line */}
                          {timelineConfig.todayPositionPercent > 0 && timelineConfig.todayPositionPercent < 100 && (
                            <div
                              style={{
                                position: 'absolute',
                                left: `${timelineConfig.todayPositionPercent}%`,
                                top: 0,
                                bottom: 0,
                                width: '2px',
                                backgroundColor: appleDesign.colors.systemRed,
                                zIndex: 15,
                                pointerEvents: 'none',
                                boxShadow: `0 0 4px ${appleDesign.colors.systemRed}40`,
                              }}
                            />
                          )}
                          <ClientTimelineBar
                            epic={epic}
                            progress={epicProgress}
                            weeksToShow={timelineConfig.weeksToShow}
                            weekOffset={timelineConfig.weekOffset}
                            todayWeekIndex={timelineConfig.todayWeekIndex}
                          />
                        </div>
                      </div>
                      );
                    })}
                  </div>
                </div>
              </div>

              {/* Legend - Apple Style */}
              <div style={{ marginTop: appleDesign.spacing.md, display: 'flex', gap: appleDesign.spacing.lg, alignItems: 'center', flexWrap: 'wrap' }}>
                {/* Today marker legend */}
                <div style={{ display: 'flex', alignItems: 'center', gap: appleDesign.spacing.xs }}>
                  <div style={{
                    width: '2px',
                    height: '14px',
                    backgroundColor: appleDesign.colors.systemRed,
                    boxShadow: `0 0 4px ${appleDesign.colors.systemRed}40`,
                  }} />
                  <span style={{ ...appleDesign.typography.caption1, color: appleDesign.colors.systemRed, fontWeight: '500' }}>Hoy</span>
                </div>
                {/* Delayed legend */}
                <div style={{ display: 'flex', alignItems: 'center', gap: appleDesign.spacing.xs }}>
                  <div style={{
                    width: '14px',
                    height: '14px',
                    backgroundColor: `${appleDesign.colors.systemRed}12`,
                    border: `2px solid ${appleDesign.colors.systemRed}`,
                    borderRadius: appleDesign.radius.sm,
                  }} />
                  <span style={{ ...appleDesign.typography.caption1, color: appleDesign.colors.systemRed, fontWeight: '500' }}>Atrasado</span>
                </div>
              </div>

              {/* Parallel work legend - Apple Style */}
              {parallelZones.length > 0 && (
                <div style={{ marginTop: appleDesign.spacing.sm, display: 'flex', gap: appleDesign.spacing.lg, alignItems: 'center' }}>
                  <span style={{ ...appleDesign.typography.caption1, color: appleDesign.colors.secondaryLabel, fontWeight: '500' }}>
                    Trabajo en paralelo:
                  </span>
                  <div style={{ display: 'flex', alignItems: 'center', gap: appleDesign.spacing.xs }}>
                    <div style={{
                      width: '14px',
                      height: '14px',
                      backgroundColor: `${appleDesign.colors.systemIndigo}10`,
                      border: `2px dashed ${appleDesign.colors.systemIndigo}`,
                      borderRadius: appleDesign.radius.sm
                    }} />
                    <span style={{ ...appleDesign.typography.caption1, color: appleDesign.colors.systemIndigo }}>2 épicas</span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: appleDesign.spacing.xs }}>
                    <div style={{
                      width: '14px',
                      height: '14px',
                      backgroundColor: `${appleDesign.colors.systemOrange}15`,
                      border: `2px dashed ${appleDesign.colors.systemOrange}`,
                      borderRadius: appleDesign.radius.sm
                    }} />
                    <span style={{ ...appleDesign.typography.caption1, color: appleDesign.colors.systemOrange }}>3+ épicas</span>
                  </div>
                </div>
              )}

              {epics.length === 0 && (
                <div style={{ textAlign: 'center', padding: appleDesign.spacing.xxl }}>
                  <div style={{ fontSize: '48px', marginBottom: appleDesign.spacing.md, opacity: 0.3 }}>◇</div>
                  <p style={{ ...appleDesign.typography.subheadline, color: appleDesign.colors.tertiaryLabel }}>
                    No hay épicas definidas en el roadmap
                  </p>
                </div>
              )}
            </>
          ) : (
            /* List View - Apple Style */
            <div style={{ display: 'flex', flexDirection: 'column', gap: appleDesign.spacing.md }}>
              {epicProgress.map((epic) => {
                const epicEndWeek = ((epic.startWeek ?? 0) - timelineConfig.weekOffset) + (epic.duration ?? 4);
                const isListDelayed = epic.progress < 100 && timelineConfig.todayWeekIndex >= epicEndWeek;

                return (
                <div
                  key={epic.id}
                  style={{
                    padding: appleDesign.spacing.md,
                    backgroundColor: isListDelayed ? `${appleDesign.colors.systemRed}06` : appleDesign.colors.secondaryBg,
                    borderRadius: appleDesign.radius.lg,
                    border: isListDelayed ? `2px solid ${appleDesign.colors.systemRed}40` : `1px solid ${appleDesign.colors.separator}`,
                    transition: appleDesign.transitions.fast,
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: appleDesign.spacing.md }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: appleDesign.spacing.md }}>
                      <div
                        style={{
                          width: '14px',
                          height: '14px',
                          borderRadius: appleDesign.radius.sm,
                          backgroundColor: isListDelayed ? appleDesign.colors.systemRed : (epic.color || appleDesign.colors.systemBlue),
                          boxShadow: `0 2px 6px ${isListDelayed ? appleDesign.colors.systemRed : (epic.color || appleDesign.colors.systemBlue)}40`,
                        }}
                      />
                      <span style={{ ...appleDesign.typography.headline, color: appleDesign.colors.label }}>{epic.name}</span>
                      {isListDelayed && (
                        <span
                          style={{
                            ...appleDesign.typography.caption1,
                            fontWeight: '600',
                            color: appleDesign.colors.systemRed,
                            backgroundColor: `${appleDesign.colors.systemRed}15`,
                            padding: '2px 10px',
                            borderRadius: appleDesign.radius.full,
                          }}
                        >
                          Atrasado
                        </span>
                      )}
                    </div>
                    <span
                      style={{
                        ...appleDesign.typography.headline,
                        color: isListDelayed ? appleDesign.colors.systemRed : (epic.color || appleDesign.colors.systemBlue),
                        padding: '4px 12px',
                        backgroundColor: `${isListDelayed ? appleDesign.colors.systemRed : (epic.color || appleDesign.colors.systemBlue)}10`,
                        borderRadius: appleDesign.radius.full,
                      }}
                    >
                      {Math.round(epic.progress)}%
                    </span>
                  </div>
                  {/* Apple-style progress bar */}
                  <div
                    style={{
                      backgroundColor: appleDesign.colors.separator,
                      borderRadius: appleDesign.radius.full,
                      height: '10px',
                      overflow: 'hidden',
                      boxShadow: 'inset 0 1px 2px rgba(0,0,0,0.1)',
                    }}
                  >
                    <div
                      style={{
                        height: '100%',
                        background: isListDelayed
                          ? `linear-gradient(90deg, ${appleDesign.colors.systemRed} 0%, ${appleDesign.colors.systemOrange} 100%)`
                          : `linear-gradient(90deg, ${epic.color || appleDesign.colors.systemBlue} 0%, ${epic.color || appleDesign.colors.systemTeal} 100%)`,
                        width: `${epic.progress}%`,
                        borderRadius: appleDesign.radius.full,
                        transition: 'width 0.8s cubic-bezier(0.34, 1.56, 0.64, 1)',
                        boxShadow: `0 0 8px ${isListDelayed ? appleDesign.colors.systemRed : (epic.color || appleDesign.colors.systemBlue)}50`,
                      }}
                    />
                  </div>
                  <div style={{ marginTop: appleDesign.spacing.sm, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span style={{ ...appleDesign.typography.footnote, color: appleDesign.colors.secondaryLabel }}>
                      <strong style={{ color: appleDesign.colors.systemGreen }}>{epic.completedCount}</strong> de {epic.taskCount} tareas completadas
                    </span>
                    {epic.progress === 100 && (
                      <span
                        style={{
                          ...appleDesign.typography.caption1,
                          color: appleDesign.colors.systemGreen,
                          fontWeight: '600',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '4px',
                        }}
                      >
                        <span>✓</span> Completado
                      </span>
                    )}
                    {isListDelayed && (
                      <span
                        style={{
                          ...appleDesign.typography.caption1,
                          color: appleDesign.colors.systemRed,
                          fontWeight: '600',
                        }}
                      >
                        Fecha planificada superada
                      </span>
                    )}
                  </div>
                </div>
                );
              })}
              {epicProgress.length === 0 && (
                <div style={{ textAlign: 'center', padding: appleDesign.spacing.xxl }}>
                  <div style={{ fontSize: '48px', marginBottom: appleDesign.spacing.md, opacity: 0.3 }}>◇</div>
                  <p style={{ ...appleDesign.typography.subheadline, color: appleDesign.colors.tertiaryLabel }}>
                    No hay épicas definidas
                  </p>
                </div>
              )}
            </div>
          )}
        </GlassCard>
      </main>
    </div>
  );
}

ClientPortal.propTypes = {
  project: PropTypes.object,
  onBack: PropTypes.func.isRequired,
  isPublicPortal: PropTypes.bool,
  portalToken: PropTypes.string,
  clientInfo: PropTypes.shape({
    name: PropTypes.string,
    logo: PropTypes.string,
    company: PropTypes.string,
  }),
};
